import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { catchError, debounceTime, of, Subscription, filter, switchMap, take } from 'rxjs';
import { AjaxService } from '../../../services/ajax.service';
import { DirectionService } from '../../../services/direction.service';
import { TranslateService } from '../../../services/translate.service';
import { ViewScopeService, Customer } from '../../../services/view-scope.service';
import { LayoutService } from '../../../layout/service/layout.service';

import { DISP_FILE_CLASSIFICATION } from '../../../services/account-api.constants';
interface FileClassificationRow {
    FileGradeClassificationRecID: number;
    Descr: string;
    countFiles: number;
    IconGrade: string;
}

interface FileClassificationSegment {
    id: number;
    label: string;
    files: number;
    icon: string;
    color: string;
    percent: number;
}

@Component({
    standalone: true,
    selector: 'app-file-processed-widget',
    imports: [CommonModule, ChartModule],
    templateUrl: './fileprocessedwidget.html',
    styleUrl: './fileprocessedwidget.scss'
})
export class FileProcessedWidget implements OnInit, OnDestroy {
    classifications: FileClassificationSegment[] = [];
    chartData: any;
    chartOptions: any;
    chartPlugins: any[] = [];
    totalFiles = 0;
    loading = false;
    error: string | null = null;
    subscription: Subscription;

    isRtl = true;

    constructor(
        private ajaxService: AjaxService,
        private layoutService: LayoutService,
        private directionService: DirectionService,
        public  viewScopeService: ViewScopeService,
        public  translateServ: TranslateService
    ) {
        this.isRtl = this.directionService.isRtl();
        this.subscription = this.layoutService.configUpdate$.pipe(debounceTime(25)).subscribe(() => {
            this.isRtl = this.directionService.isRtl();
            this.setChart();
        });
    }

    ngOnInit(): void {
        this.loadFileClassifications();
    }

    // Waits for ViewScopeService's selectedCustomer$ (BehaviorSubject, always replays its latest
    // value) instead of reading it synchronously - avoids racing SelectAccountComponent, which
    // populates the selected customer asynchronously on the same tick this widget mounts.
    private loadFileClassifications(): void {
        this.loading = true;
        this.error = null;

        const userId = this.viewScopeService.getCurrentOpertaingUserId();

        this.viewScopeService.selectedCustomer$.pipe(
            filter((customer): customer is Customer => customer != null),
            take(1),
            switchMap(customer => {
                const apiUrl = this.ajaxService.getBaseOfApi() + DISP_FILE_CLASSIFICATION + customer.id + '/' + userId;
                return this.ajaxService.getData(apiUrl);
            }),
            catchError(error => {
                console.error('FileProcessedWidget - API error', error);
                this.error = this.translateServ.dictionary['FAILED_LOAD_DATA'];
                this.loading = false;
                return of({ Table: [] });
            })
        ).subscribe((response: any) => {
            this.loading = false;
            this.setClassifications(response?.Table ?? []);
        });
    }

    private setClassifications(rows: FileClassificationRow[]): void {
        const documentStyle = getComputedStyle(document.documentElement);
        
        // Primary main color base
        const primaryColor = documentStyle.getPropertyValue('--p-primary-color').trim()|| this.layoutService.layoutConfig().primary|| '#3b82f6';

        // 75% opacity and 40% opacity variants of primary color
        const color75 = primaryColor.startsWith('#')
            ? `${primaryColor}BF` // BF in HEX = ~75% alpha
            : primaryColor.replace('rgb', 'rgba').replace(')', ', 0.75)');

        const color40 = primaryColor.startsWith('#')
            ? `${primaryColor}66` // 66 in HEX = ~40% alpha
            : primaryColor.replace('rgb', 'rgba').replace(')', ', 0.40)');

        const colors = [color75, color40];

        this.totalFiles = rows.reduce((sum, row) => sum + Number(row.countFiles || 0), 0);

        if (this.totalFiles <= 0) {
            this.classifications = [];
            this.setChart();
            return;
        }

        this.classifications = rows.map((row, index) => {
            const files = Number(row.countFiles || 0);

            return {
                id: row.FileGradeClassificationRecID,
                label: row.Descr,
                files,
                icon: row.IconGrade,
                color: colors[index % colors.length],
                percent: Math.round((files / this.totalFiles) * 100)
            };
        });

        this.setChart();
    }

    private setChart(): void {
        const documentStyle = getComputedStyle(document.documentElement);
        const surfaceCard = documentStyle.getPropertyValue('--p-content-background').trim() || '#ffffff';
        const primaryColor = documentStyle.getPropertyValue('--p-primary-color').trim()
            || this.layoutService.layoutConfig().primary
            || '#3b82f6';

        const withOpacity = (color: string, opacity: number) => color.startsWith('#')
            ? `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`
            : color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);

        const chartColors = [primaryColor, withOpacity(primaryColor, 0.65), withOpacity(primaryColor, 0.35)];

        // Helper function requested to resolve dynamic text color
        const getResolvedTextColor = () => {
            const style = getComputedStyle(document.documentElement);
            return style.getPropertyValue('--text-color').trim()
                || style.getPropertyValue('--p-text-color').trim()
                || (this.layoutService.isDarkTheme() ? '#ffffff' : '#111827');
        };

        const textColor = getResolvedTextColor();

        this.chartData = {
            labels: this.classifications.map(classification => `${classification.label} (${classification.percent}%)`),
            datasets: [
                {
                    data: this.classifications.map(classification => classification.files),
                    backgroundColor: this.classifications.map((classification, index) => chartColors[index % chartColors.length]),
                    hoverBackgroundColor: this.classifications.map((classification, index) => chartColors[index % chartColors.length]),
                    borderColor: surfaceCard,
                    borderWidth: 3
                }
            ]
        };

        this.chartOptions = {
            maintainAspectRatio: false,
            cutout: '68%',
            plugins: {
                legend: {
                    position: this.isRtl ? 'right' : 'left',
                    labels: {
                        color: textColor,
                        usePointStyle: true,
                        pointStyle: 'circle',
                        padding: 18
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (context: any) => {
                            const classification = this.classifications[context.dataIndex];
                            return `${classification.label}: ${classification.files} (${this.translateServ.dictionary['FILES']}) (${classification.percent}%)`;
                        }
                    }
                }
            },
            color: textColor
        };

        const totalFilesVal = this.totalFiles;
        this.chartPlugins = [{
            id: 'fileClassificationCenterText',
            afterDraw: (chart: any) => {
                if (!chart.getDatasetMeta(0)?.data?.length) return;

                const { ctx } = chart;
                const meta = chart.getDatasetMeta(0);
                const x = meta.data[0].x;
                const y = meta.data[0].y;

                const dynamicTextColor = getResolvedTextColor();

                ctx.save();
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                ctx.font = '13px sans-serif';
                ctx.fillStyle = dynamicTextColor;
                ctx.fillText(this.translateServ.dictionary['TOTAL_FILES'], x, y - 10);

                ctx.font = 'bold 24px sans-serif';
                ctx.fillStyle = dynamicTextColor;
                ctx.fillText(totalFilesVal.toString(), x, y + 15);

                ctx.restore();
            }
        }];
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}